import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconCalendar,
  IconCheck,
  IconClock,
  IconInfo,
  IconMapPin,
  IconPaperclip,
  IconPhone,
  IconTag,
  IconUser,
} from './ticket-details-icons';
import {
  fetchTicketById,
  getTicketPriorities,
  getTicketStatuses,
  getTicketTeams,
  humanizeError,
  updateTicket,
} from '../services/api';
import Dropdown from '../components/global/Dropdown';
import Button from '../components/global/Button';
import './TicketDetails.css';

function formatRaisedOn(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-IN', { month: 'short' });
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
}

function formatTimelineStamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-IN', { month: 'short' });
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${day} ${month}, ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
}

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / (60 * 1000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatRaisedOn(iso);
}

function initialsFor(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function titleCaseIssueType(s) {
  if (!s) return '—';
  return String(s)
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${bytes} B`;
}

function buildTimeline(ticket) {
  const raisedMs = new Date(ticket.raisedAt || ticket.createdAt).getTime();
  const assignedAt = new Date(raisedMs + 1.5 * 60 * 60 * 1000).toISOString();
  const investigationAt = new Date(raisedMs + 20 * 60 * 60 * 1000).toISOString();
  const resolvedAt = new Date(raisedMs + 48 * 60 * 60 * 1000).toISOString();
  const closedAt = new Date(raisedMs + 72 * 60 * 60 * 1000).toISOString();
  const { status } = ticket;

  const steps = [
    {
      key: 'created',
      title: 'Ticket Created',
      body: 'Ticket has been registered',
      tone: 'done',
      at: ticket.raisedAt || ticket.createdAt,
    },
    {
      key: 'assigned',
      title: 'Assigned to Technician',
      body: `${ticket.fieldOfficer} will handle request`,
      tone: status === 'Open' ? 'pending' : 'done',
      at: status === 'Open' ? null : assignedAt,
    },
    {
      key: 'investigation',
      title: 'Under Investigation',
      body: 'Technician inspecting the area',
      tone:
        status === 'In Progress'
          ? 'active'
          : status === 'Open'
            ? 'pending'
            : 'done',
      at:
        status === 'In Progress'
          ? investigationAt
          : status === 'Open'
            ? null
            : investigationAt,
    },
    {
      key: 'resolved',
      title: 'Issue Resolved',
      body:
        status === 'Resolved' || status === 'Closed'
          ? 'Resolution documented'
          : 'Pending technician update',
      tone:
        status === 'Resolved' || status === 'Closed'
          ? 'done'
          : 'pending',
      at: status === 'Resolved' || status === 'Closed' ? resolvedAt : null,
    },
    {
      key: 'closed',
      title: 'Ticket Closed',
      body:
        status === 'Closed'
          ? 'Ticket archived'
          : 'Awaiting field officer confirmation',
      tone: status === 'Closed' ? 'done' : 'pending',
      at: status === 'Closed' ? closedAt : null,
    },
  ];

  return steps;
}

function downloadMockFile(name) {
  const blob = new Blob([`Preview placeholder for ${name}`], {
    type: 'text/plain;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.click();
  URL.revokeObjectURL(url);
}

export default function TicketDetails() {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);

  const [draftStatus, setDraftStatus] = useState('');
  const [draftAssign, setDraftAssign] = useState('');
  const [draftPriority, setDraftPriority] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchTicketById(ticketId);
      setTicket(data);
      setDraftStatus(data.status);
      setDraftAssign(data.assignedTo);
      setDraftPriority(data.priority || 'Medium');
    } catch (err) {
      setError(humanizeError(err, 'Could not load this ticket. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  const statusOptions = useMemo(
    () => getTicketStatuses().map((s) => ({ value: s, label: s })),
    [],
  );
  const teamOptions = useMemo(
    () => getTicketTeams().map((s) => ({ value: s, label: s })),
    [],
  );
  const priorities = useMemo(() => getTicketPriorities(), []);

  const hasDirty = useMemo(() => {
    if (!ticket) return false;
    return (
      draftStatus !== ticket.status ||
      draftAssign !== ticket.assignedTo ||
      draftPriority !== (ticket.priority || 'Medium')
    );
  }, [ticket, draftStatus, draftAssign, draftPriority]);

  const handleSave = async () => {
    if (!ticket || !hasDirty) return;
    setSaveBusy(true);
    setError('');
    try {
      const updated = await updateTicket(ticket.id, {
        status: draftStatus,
        assignedTo: draftAssign,
        priority: draftPriority,
      });
      setTicket(updated);
      setDraftStatus(updated.status);
      setDraftAssign(updated.assignedTo);
      setDraftPriority(updated.priority || 'Medium');
    } catch (err) {
      setError(humanizeError(err, 'Could not save the changes. Please try again.'));
    } finally {
      setSaveBusy(false);
    }
  };

  const timeline = useMemo(() => (ticket ? buildTimeline(ticket) : []), [ticket]);

  const statusSlug = (ticket?.status || 'open')
    .toLowerCase()
    .replace(/\s+/g, '-');

  if (loading) {
    return (
      <div className="td-page">
        <div className="td-state">Loading ticket...</div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="td-page">
        <div className="td-state td-state--error">
          <p>{error || 'Ticket not found.'}</p>
          <Button onClick={() => navigate('/tickets')}>Back to Tickets</Button>
        </div>
      </div>
    );
  }

  const priorityKey = (ticket.priority || 'medium').toLowerCase();
  const rawLocation = ticket.locationLabel || ticket.location;
  const locationDisplay =
    rawLocation == null || rawLocation === ''
      ? '—'
      : String(rawLocation).replace(/\s*[-–—]\s*/g, ' · ');
  const raisedSource = ticket.raisedAt || ticket.createdAt;

  const metaItems = [
    {
      k: 'officer',
      Icon: IconUser,
      label: 'Field Officer',
      value: ticket.fieldOfficer || '—',
    },
    {
      k: 'contact',
      Icon: IconPhone,
      label: 'Contact',
      value: ticket.fieldOfficerPhone || '—',
    },
    {
      k: 'category',
      Icon: IconTag,
      label: 'Category',
      value: ticket.category || '—',
    },
    {
      k: 'location',
      Icon: IconMapPin,
      label: 'Location',
      value: locationDisplay,
    },
    {
      k: 'raised',
      Icon: IconCalendar,
      label: 'Raised On',
      value: formatRaisedOn(raisedSource),
    },
    {
      k: 'updated',
      Icon: IconClock,
      label: 'Last Updated',
      value: relativeTime(ticket.updatedAt),
    },
    {
      k: 'assigned',
      Icon: IconUser,
      label: 'Assigned To',
      value: ticket.assignedTo || '—',
    },
    {
      k: 'issue',
      Icon: IconInfo,
      label: 'Issue Type',
      value: titleCaseIssueType(ticket.issueType),
    },
  ];

  return (
    <div className="td-page">
      <header className="td-topbar">
        <h1 className="td-page-title">Tickets</h1>
        <span className={`td-open-badge td-open-badge--${statusSlug}`}>
          <span className="td-open-badge-dot" aria-hidden="true" />
          {ticket.status}
        </span>
      </header>

      {error && ticket ? (
        <div className="td-banner" role="alert">
          {error}
        </div>
      ) : null}

      <div className="td-layout">
        <div className="td-row">
          <div className="td-row__main">
            <article className="td-surface td-card td-card-ticket">
              <div className="td-ticket-stack">
                <div className="td-ticket-lead">
                  <div className="td-ticket-top">
                    <div className="td-ticket-id-stack">
                      <p className="td-overline">Ticket ID</p>
                      <p className="td-ticket-id-hero">{ticket.id}</p>
                    </div>
                    <span className={`td-priority-tag td-priority-tag--${priorityKey}`}>
                      {ticket.priority || 'Medium'}
                    </span>
                  </div>

                  <h2 className="td-subject">{ticket.title}</h2>
                  <p className="td-description">
                    {ticket.description || 'No description provided.'}
                  </p>
                </div>

                <div className="td-ticket-meta-wrap">
                  <div className="td-rule" />

                  <div className="td-meta-grid">
                    {metaItems.map(({ k, Icon, label, value }) => (
                      <div key={k} className="td-meta-cell">
                        <div className="td-meta-kicker">
                          <Icon className="td-ico td-ico--meta" aria-hidden="true" />
                          <span className="td-meta-label">{label}</span>
                        </div>
                        <p className="td-meta-value">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div className="td-row__aside">
            <article className="td-surface td-card td-card-side">
              <div className="td-card-side-shell">
                <div className="td-card-side-upper">
                  <h2 className="td-card-title">Status &amp; Assignment</h2>
                  <div className="td-card-side-fields">
                    <div className="td-status-field td-status-field--current">
                      <label className="td-field-label" htmlFor="td-status-dd">
                        Current Status
                      </label>
                      <Dropdown
                        options={statusOptions}
                        value={draftStatus}
                        onChange={setDraftStatus}
                        ariaLabel="Current status"
                        disabled={saveBusy}
                        rootClassName="td-dd"
                      />
                    </div>
                    <div className="td-status-field td-status-field--assign">
                      <label className="td-field-label" htmlFor="td-assign-dd">
                        Assign To
                      </label>
                      <Dropdown
                        options={teamOptions}
                        value={draftAssign}
                        onChange={setDraftAssign}
                        ariaLabel="Assign to"
                        disabled={saveBusy}
                        rootClassName="td-dd"
                      />
                    </div>
                    <div className="td-status-field td-status-field--priority">
                      <p className="td-field-label">Priority</p>
                      <div className="td-segments" role="group" aria-label="Priority">
                        {priorities.map((p) => (
                          <button
                            key={p}
                            type="button"
                            className={`td-seg ${draftPriority === p ? 'td-seg--on' : ''} td-seg--${p.toLowerCase()}`}
                            onClick={() => setDraftPriority(p)}
                            disabled={saveBusy}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="primary"
                  className="td-save"
                  onClick={handleSave}
                  disabled={saveBusy || !hasDirty}
                >
                  {saveBusy ? 'Saving…' : 'Save Update'}
                </Button>
              </div>
            </article>
          </div>
        </div>

        <div className="td-row">
          <div className="td-row__main">
            <article className="td-surface td-card td-card-timeline">
              <div className="td-timeline-head">
                <h2 className="td-card-title">Ticket Timeline</h2>
                <span className="td-activity-hint">Activity tracker</span>
              </div>
              <ul className="td-timeline">
                {timeline.map((step) => (
                  <li key={step.key} className={`td-tl-item td-tl-item--${step.tone}`}>
                    <div className="td-tl-rail" aria-hidden="true">
                      <span className="td-tl-dot">
                        {step.tone === 'pending' ? null : (
                          <span className="td-tl-dot-ring">
                            {step.tone === 'done' ? <IconCheck /> : <IconClock />}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="td-tl-body">
                      <div className="td-tl-title-row">
                        <p className="td-tl-title">{step.title}</p>
                        {step.at ? (
                          <time className="td-tl-time" dateTime={step.at}>
                            {formatTimelineStamp(step.at)}
                          </time>
                        ) : (
                          <span className="td-tl-time td-tl-time--empty" />
                        )}
                      </div>
                      <p className="td-tl-sub">{step.body}</p>
                      {step.tone === 'active' ? (
                        <span className="td-tl-pill">
                          <span className="td-tl-pill-dot" aria-hidden="true" />
                          <span className="td-tl-pill-label">In progress</span>
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <div className="td-row__aside td-row__aside--stack">
            <article className="td-surface td-card td-card-officer">
              <h2 className="td-card-title">Field Officer</h2>
              <div className="td-officer-stack">
                <div className="td-officer-row">
                  <div className="td-officer-avatar" aria-hidden="true">
                    {initialsFor(ticket.fieldOfficer)}
                  </div>
                  <div className="td-officer-text">
                    <p className="td-officer-name">{ticket.fieldOfficer}</p>
                    <p className="td-officer-phone">{ticket.fieldOfficerPhone}</p>
                  </div>
                </div>
                <a
                  className="td-call-btn"
                  href={`tel:${String(ticket.fieldOfficerPhone || '').replace(/\s/g, '')}`}
                >
                  <IconPhone aria-hidden="true" />
                  Call
                </a>
              </div>
            </article>

            <article className="td-surface td-card td-card-attach">
              <h2 className="td-card-title">Attachments</h2>
              <div className="td-attach-stack">
                <ul className="td-attach-list">
                  {(ticket.attachments || []).map((f) => (
                    <li key={f.id || f.name} className="td-attach-row">
                      <span className="td-attach-icon-wrap" aria-hidden="true">
                        <IconPaperclip className="td-ico td-attach-ico" aria-hidden="true" />
                      </span>
                      <div className="td-attach-meta">
                        <p className="td-attach-name">{f.name}</p>
                        <p className="td-attach-size">{formatFileSize(f.sizeBytes)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="td-download-all"
                  onClick={() =>
                    (ticket.attachments || []).forEach((f) => downloadMockFile(f.name))
                  }
                >
                  Download
                </button>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}
